import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';

import {
  Box,
  Text,
  Switch,
  useColorModeValue,
  IconButton,
  Flex,
  Spinner,
  useToast,
} from '@chakra-ui/react';

import { DeleteIcon } from '@chakra-ui/icons';

import { apiAddUserToWorkflow, apiRemoveUserFromWorkflow } from 'api';

import type { WorkflowUser } from './interfaces';

const UserCard = ({
  user,
  users,
}: {
  user: WorkflowUser;
  users: WorkflowUser[];
}) => {
  const { id: workflowId } = useParams();
  const toast = useToast();

  const [isRemoved, setIsRemoved] = useState(false);

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const boxBgColor = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  // State to track which users have notifications enabled
  const [notificationStatus, setNotificationStatus] = useState<
    Record<number, boolean>
  >(
    users.reduce((acc, user) => {
      acc[user.id] = user.has_new_lead_email;
      return acc;
    }, {} as Record<number, boolean>),
  );

  // Update notification settings mutation
  const updateNotificationMutation = useMutation({
    mutationFn: ({
      userId,
      hasNotifications,
    }: {
      userId: number;
      hasNotifications: boolean;
    }) => {
      return apiAddUserToWorkflow(parseInt(workflowId), userId, {
        has_new_lead_email: hasNotifications,
      });
    },
    onSuccess: () => {
      toast({
        title: 'Notifications updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update notifications',
        description: error.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  // Remove user from workflow mutation
  const removeUserMutation = useMutation({
    mutationFn: (userId: number) =>
      apiRemoveUserFromWorkflow(parseInt(workflowId), userId),
    onSuccess: () => {
      toast({
        title: 'User removed from workflow',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      setIsRemoved(true);
    },
    onError: (error) => {
      toast({
        title: 'Failed to remove user',
        description: error.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  // Handle notification toggle
  const handleNotificationToggle = (
    userId: number,
    workflowUserId: number,
    newStatus: boolean,
  ) => {
    setNotificationStatus((prev) => ({
      ...prev,
      [workflowUserId]: newStatus,
    }));

    updateNotificationMutation.mutate({
      userId,
      hasNotifications: newStatus,
    });
  };

  if (isRemoved) return null;

  return (
    <Box
      key={user.id}
      borderWidth="1px"
      borderColor={borderColor}
      bg={boxBgColor}
      borderRadius="lg"
      p={3}
      shadow="sm"
    >
      <Flex justify="space-between" align="center">
        <Text fontWeight="500" color={textColor}>
          {user.user.first_name} {user.user.last_name}
        </Text>
        <Flex align="center" gap={2}>
          <Text fontSize="sm" color="gray.500">
            New lead notification
          </Text>

          {updateNotificationMutation.isPending ? (
            <Spinner size="sm" color="teal.500" />
          ) : (
            <Switch
              size="sm"
              isChecked={notificationStatus[user.id]}
              onChange={(e) =>
                handleNotificationToggle(
                  user.user.id,
                  user.id,
                  e.target.checked,
                )
              }
              colorScheme="teal"
            />
          )}

          <IconButton
            aria-label="Remove user from workflow"
            icon={<DeleteIcon />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={() => removeUserMutation.mutate(user.user.id)}
            isLoading={removeUserMutation.isPending}
          />
        </Flex>
      </Flex>
    </Box>
  );
};

export default UserCard;
